import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import styled from 'styled-components';
import PaymentModal from '../components/common/PaymentModal';
import { AuthContext } from '../components/auth/AuthProvider';
import axios from 'axios';

const PageContainer = styled.div`
  padding: 40px 0;
`;

const Container = styled.div.attrs({ className: 'app-container' })`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled.h1`
  text-align: center;
  margin-bottom: 40px;
  font-weight: light;
  
  &::after {
    content: '';
    display: block;
    width: 50px;
    height: 3px;
    background-color: #A961ED;
    margin: 15px auto 0;
  }
`;

const CheckoutContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 30px;
  
  @media (min-width: 992px) {
    grid-template-columns: 1.5fr 1fr;
  }
`;

const FormContainer = styled.div`
  background-color: rgba(0, 0, 0, 0.3);
  padding: 30px;
  border-radius: 8px;
`;

const OrderSummaryContainer = styled.div`
  background-color: rgba(0, 0, 0, 0.3);
  padding: 30px;
  border-radius: 8px;
  align-self: start;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  font-size: 14px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid white;
  border-radius: 8px;
  background-color: transparent;
  color: white;
  
  &:focus {
    outline: none;
    border-color: #A961ED;
  }
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  margin-top: 5px;
  font-size: 0.9rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const FormSection = styled.div`
  margin-bottom: 30px;
  
  h3 {
    margin-bottom: 15px;
    position: relative;
    padding-bottom: 10px;
    font-size: 20px;
    font-weight: 300;
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 30px;
      height: 2px;
      background-color: #A961ED;
    }
  }
`;

const SummaryTitle = styled.h3`
  margin-bottom: 20px;
  position: relative;
  padding-bottom: 10px;
  font-size: 20px;
  font-weight: 300;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 30px;
    height: 2px;
    background-color: #A961ED;
  }
`;

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
  font-size: 0.95rem;
  
  .item-name {
    width: 70%;
  }
  
  .item-price {
    width: 30%;
    text-align: right;
    font-weight: 500;
  }
`;

const SummaryDivider = styled.hr`
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin: 15px 0;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 15px;
  font-size: 1.2rem;
  font-weight: 600;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 15px;
  background-color: #A961ED;
  color: white;
  border: none;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 30px;
  
  &:hover:not(:disabled) {
    background-color: #9050c9;
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const BackButton = styled.button`
  width: 100%;
  padding: 15px;
  background-color: transparent;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 15px;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const PaymentContainer = styled.div`
  background-color: rgba(0, 0, 0, 0.3);
  padding: 30px;
  border-radius: 8px;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
`;

const PaymentTitle = styled.h3`
  margin-bottom: 25px;
  text-align: center;
  font-size: 24px;
  font-weight: 300;
  
  &::after {
    content: '';
    display: block;
    width: 40px;
    height: 2px;
    background-color: #A961ED;
    margin: 15px auto 0;
  }
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: 40px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  margin-bottom: 40px;
  background-color: rgba(0, 0, 0, 0.3);

  p {
    margin-bottom: 20px;
  }
`;

const ContinueShoppingLink = styled(Link)`
  display: inline-block;
  padding: 15px 30px;
  background-color: #A961ED;
  color: white;
  text-align: center;
  border-radius: 50px;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #9050c9;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 20px;
`;

const Checkbox = styled.input`
  margin-right: 10px;
  margin-top: 3px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  font-size: 14px;
  cursor: pointer;
`;

const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { register, isAuthenticated, user } = useContext(AuthContext);
  const [orderDetails, setOrderDetails] = useState(null);
  const [customer, setCustomer] = useState({
    first_name: '',
    last_name: '',
    email: ''
  });
  const [createAccount, setCreateAccount] = useState(false);
  const [registrationData, setRegistrationData] = useState({
    username: '',
    password: '',
    confirm_password: '',
    accept_terms: false
  });
  const [registrationErrors, setRegistrationErrors] = useState({});
  const [errors, setErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  // Get order details from location state or fetch from API
  useEffect(() => {
    // Load cart items from localStorage
    try {
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        const parsedCart = JSON.parse(savedCart);
        setCartItems(parsedCart);

        // Create order details from cart
        setOrderDetails({
          id: 'ORDER' + Math.floor(Math.random() * 10000),
          items: parsedCart
        });
      } else {
        setCartItems([]);
      }
    } catch (err) {
      console.error('Error fetching cart:', err);
      setCartItems([]);
    }
  }, [location]);

  // Pre-fill customer information if user is logged in
  useEffect(() => {
    if (isAuthenticated && user) {
      setCustomer({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || ''
      });
    }
  }, [isAuthenticated, user]);

  // Validate customer form
  useEffect(() => {
    validateForm();
  }, [customer, createAccount, registrationData]);

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomer(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear validation error when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  // Handle input change for registration fields
  const handleRegistrationInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setRegistrationData(prev => ({
      ...prev,
      [name]: fieldValue
    }));

    // Clear validation error when user types
    if (registrationErrors[name]) {
      setRegistrationErrors({
        ...registrationErrors,
        [name]: ''
      });
    }
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};

    // Basic validation for required fields
    if (!customer.first_name) newErrors.first_name = 'First name is required';
    if (!customer.last_name) newErrors.last_name = 'Last name is required';
    if (!customer.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(customer.email)) {
      newErrors.email = 'Email is invalid';
    }

    setErrors(newErrors);

    // Additional validation for registration fields
    const newRegistrationErrors = {};
    if (createAccount) {
      if (!registrationData.username) {
        newRegistrationErrors.username = 'Username is required';
      }
      if (!registrationData.password) {
        newRegistrationErrors.password = 'Password is required';
      } else if (registrationData.password.length < 6) {
        newRegistrationErrors.password = 'Password must be at least 6 characters';
      }
      if (!registrationData.confirm_password) {
        newRegistrationErrors.confirm_password = 'Please confirm your password';
      } else if (registrationData.password !== registrationData.confirm_password) {
        newRegistrationErrors.confirm_password = 'Passwords do not match';
      }
      if (!registrationData.accept_terms) {
        newRegistrationErrors.accept_terms = 'You must accept the Terms and Conditions';
      }
    }

    setRegistrationErrors(newRegistrationErrors);

    const isValid = Object.keys(newErrors).length === 0 &&
      (!createAccount || Object.keys(newRegistrationErrors).length === 0);

    setIsFormValid(isValid);
    return isValid;
  };

  // Register the user before proceeding to payment
  const handleRegisterUser = async () => {
    try {
      const result = await register(
        registrationData.username,
        customer.email,
        registrationData.password,
        customer.first_name,
        customer.last_name
      );

      if (result.success) {
        setRegistrationSuccess(true);
        return true;
      } else {
        setRegistrationErrors({
          ...registrationErrors,
          general: result.message || 'Registration failed'
        });
        return false;
      }
    } catch (error) {
      setRegistrationErrors({
        ...registrationErrors,
        general: 'An error occurred during registration'
      });
      return false;
    }
  };

  // Handle payment success
  const handlePaymentSuccess = (data) => {
    console.log('Payment successful:', data);

    // Clear cart after successful payment
    localStorage.removeItem('cart');

    navigate('/payment/success', {
      state: {
        transactionId: data.transaction_id,
        orderDetails
      }
    });
  };

  // Handle payment error
  const handlePaymentError = (error) => {
    console.error('Payment failed:', error);
    navigate('/payment/failed', {
      state: {
        error: error.message
      }
    });
  };

  // Update proceed to payment to include registration if needed
  const handleProceedToPayment = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setIsSubmitting(true);

      // Register user if checkbox is checked and user is not already authenticated
      let canProceed = true;
      if (createAccount && !isAuthenticated && !registrationSuccess) {
        canProceed = await handleRegisterUser();
      }

      if (canProceed) {
        // Create order in database
        try {
          const response = await axios.post(
            `${process.env.REACT_APP_API_URL || ''}/orders/create`,
            { items: cartItems },
            {
              headers: {
                'X-User-ID': user?.id,
                'Content-Type': 'application/json'
              }
            }
          );

          if (response.data.success) {
            // Update orderDetails with real order ID from database
            setOrderDetails({
              id: response.data.order_id,
              items: cartItems
            });

            // Open the payment modal
            setIsPaymentModalOpen(true);
          } else {
            // Display error
            console.error('Failed to create order:', response.data.message);
            alert('Error creating order: ' + response.data.message);
          }
        } catch (error) {
          console.error('Error creating order:', error);

          // Improved error handling
          let errorMessage = 'Error creating order. Please try again.';
          if (error.response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            console.error('Server responded with error:', error.response.data);
            console.error('Status code:', error.response.status);
            errorMessage = error.response.data?.message || errorMessage;
          } else if (error.request) {
            // The request was made but no response was received
            console.error('No response received:', error.request);
            errorMessage = 'No response from server. Please check your connection.';
          } else {
            // Something happened in setting up the request that triggered an Error
            console.error('Error setting up request:', error.message);
            errorMessage = `Request error: ${error.message}`;
          }

          alert(errorMessage);
        }
      }

      setTimeout(() => {
        setIsSubmitting(false);
      }, 300);
    }
  };

  // Calculate order total
  const calculateTotal = () => {
    if (!cartItems || cartItems.length === 0) return 0;
    return cartItems.reduce((total, item) => {
      const itemQuantity = item.quantity || 1;
      return total + (parseFloat(item.price) * itemQuantity);
    }, 0);
  };

  if (!orderDetails && cartItems.length === 0) {
    return (
      <PageContainer>
        <Container>
          <SectionTitle>Checkout</SectionTitle>
          <EmptyCart>
            <p>Your cart is empty.</p>
            <ContinueShoppingLink to="/create">Start Designing Your Star Map</ContinueShoppingLink>
          </EmptyCart>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container>
        <SectionTitle>Checkout</SectionTitle>

        <form onSubmit={handleProceedToPayment}>
          <CheckoutContainer>
            <FormContainer>
              <FormSection>
                <h3>Contact Information</h3>
                <FormRow>
                  <FormGroup>
                    <Label htmlFor="first_name">First Name</Label>
                    <Input
                      type="text"
                      id="first_name"
                      name="first_name"
                      value={customer.first_name}
                      onChange={handleInputChange}
                    />
                    {errors.first_name && <ErrorMessage>{errors.first_name}</ErrorMessage>}
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input
                      type="text"
                      id="last_name"
                      name="last_name"
                      value={customer.last_name}
                      onChange={handleInputChange}
                    />
                    {errors.last_name && <ErrorMessage>{errors.last_name}</ErrorMessage>}
                  </FormGroup>
                </FormRow>

                <FormGroup>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    value={customer.email}
                    onChange={handleInputChange}
                  />
                  {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
                </FormGroup>

                {!isAuthenticated && (
                  <CheckboxContainer>
                    <Checkbox
                      type="checkbox"
                      id="create_account"
                      checked={createAccount}
                      onChange={() => setCreateAccount(!createAccount)}
                    />
                    <CheckboxLabel htmlFor="create_account">
                      Create an account for faster checkout next time
                    </CheckboxLabel>
                  </CheckboxContainer>
                )}

                {createAccount && !isAuthenticated && !registrationSuccess && (
                  <FormSection>
                    <h3>Create Account</h3>
                    {registrationErrors.general && (
                      <ErrorMessage style={{ marginBottom: '15px' }}>{registrationErrors.general}</ErrorMessage>
                    )}

                    <FormGroup>
                      <Label htmlFor="username">Username</Label>
                      <Input
                        type="text"
                        id="username"
                        name="username"
                        value={registrationData.username}
                        onChange={handleRegistrationInputChange}
                      />
                      {registrationErrors.username && <ErrorMessage>{registrationErrors.username}</ErrorMessage>}
                    </FormGroup>

                    <FormGroup>
                      <Label htmlFor="password">Password</Label>
                      <Input
                        type="password"
                        id="password"
                        name="password"
                        value={registrationData.password}
                        onChange={handleRegistrationInputChange}
                      />
                      {registrationErrors.password && <ErrorMessage>{registrationErrors.password}</ErrorMessage>}
                    </FormGroup>

                    <FormGroup>
                      <Label htmlFor="confirm_password">Confirm Password</Label>
                      <Input
                        type="password"
                        id="confirm_password"
                        name="confirm_password"
                        value={registrationData.confirm_password}
                        onChange={handleRegistrationInputChange}
                      />
                      {registrationErrors.confirm_password && <ErrorMessage>{registrationErrors.confirm_password}</ErrorMessage>}
                    </FormGroup>

                    <CheckboxContainer>
                      <Checkbox
                        type="checkbox"
                        id="accept_terms"
                        name="accept_terms"
                        checked={registrationData.accept_terms}
                        onChange={handleRegistrationInputChange}
                      />
                      <CheckboxLabel htmlFor="accept_terms">
                        I accept the <Link to="/terms" target="_blank">Terms and Conditions</Link> and <Link to="/privacy" target="_blank">Privacy Policy</Link>
                      </CheckboxLabel>
                    </CheckboxContainer>
                    {registrationErrors.accept_terms && <ErrorMessage>{registrationErrors.accept_terms}</ErrorMessage>}
                  </FormSection>
                )}

                {registrationSuccess && (
                  <div style={{ color: '#4BB543', marginTop: '10px', padding: '10px', background: 'rgba(75, 181, 67, 0.1)', borderRadius: '5px' }}>
                    Account created successfully! You can use it to log in after checkout.
                  </div>
                )}
              </FormSection>
            </FormContainer>

            <OrderSummaryContainer>
              <SummaryTitle>Order Summary</SummaryTitle>

              {cartItems.map((item, index) => (
                <SummaryItem key={index}>
                  <div className="item-name">{item.name}</div>
                  <div className="item-price">€{parseFloat(item.price).toFixed(2)}</div>
                </SummaryItem>
              ))}

              <SummaryDivider />

              <TotalRow>
                <div>Total</div>
                <div>€{calculateTotal().toFixed(2)}</div>
              </TotalRow>

              <SubmitButton
                type="submit"
                disabled={isSubmitting || !isFormValid}
              >
                {isSubmitting ? 'Processing...' : 'Proceed to Payment'}
              </SubmitButton>
            </OrderSummaryContainer>
          </CheckoutContainer>
        </form>

        {/* Payment modal */}
        <PaymentModal
          isOpen={isPaymentModalOpen}
          onClose={() => setIsPaymentModalOpen(false)}
          orderId={orderDetails?.id}
          amount={calculateTotal() * 100} // BeGateway expects amount in cents
          currency="EUR"
          description="Star Map Purchase"
          customer={customer}
          onSuccess={handlePaymentSuccess}
          onError={handlePaymentError}
        />
      </Container>
    </PageContainer>
  );
};

export default Checkout; 